// src/Pages/Dashboard/Sales/CustomerData.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./CustomerData.css";

const countries = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "China",
  "UAE",
  "Singapore",
  "Malaysia",
  "Saudi Arabia",
  "South Africa",
  "Brazil",
  "Russia",
];

const indiaStates = [
  "Tamil Nadu",
  "Karnataka",
  "Maharashtra",
  "Delhi",
  "Gujarat",
  "Uttar Pradesh",
  "Kerala",
  "West Bengal",
  "Punjab",
  "Rajasthan",
  "Andhra Pradesh",
  "Telangana",
  "Bihar",
  "Madhya Pradesh",
  "Haryana",
];

const currencies = [
  { code: "INR", name: "Indian Rupee" },
  { code: "USD", name: "United States Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "AED", name: "UAE Dirham" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "JPY", name: "Japanese Yen" },
];

const CustomerData = () => {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== "new";

  const [activeTab, setActiveTab] = useState("other");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");

  const [form, setForm] = useState({
    customerType: "Business",
    salutation: "Mr.",
    firstName: "",
    lastName: "",
    companyName: "",
    displayName: "",
    email: "",
    workPhone: "",
    mobile: "",
    language: "English",
    pan: "",
    currency: "INR",
    openingBalance: "",
    paymentTerms: "Due on Receipt",
    enablePortal: false,
    remarks: "",
    billing: {
      attention: "",
      country: "India",
      street1: "",
      street2: "",
      city: "",
      state: "Tamil Nadu",
      pincode: "",
      phone: "",
      fax: "",
    },
    shipping: {
      attention: "",
      country: "India",
      street1: "",
      street2: "",
      city: "",
      state: "Tamil Nadu",
      pincode: "",
      phone: "",
      fax: "",
    },
    contacts: [
      {
        salutation: "Mr.",
        firstName: "",
        lastName: "",
        email: "",
        workPhone: "",
        mobile: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});

  // Load customer on edit
  useEffect(() => {
    if (isEdit && userId) {
      const loadCustomer = async () => {
        try {
          setLoading(true);
          setError("");
          const res = await fetch(
            `${backendUrl}/api/customers/get?userId=${userId}`
          );
          const result = await res.json();

          if (result.success) {
            const customer = result.data.find((c) => c._id === id);
            if (customer) {
              setForm({
                ...customer,
                openingBalance: customer.openingBalance || "",
                // Ensure nested objects exist
                billing: customer.billing || form.billing,
                shipping: customer.shipping || form.shipping,
                contacts:
                  customer.contacts && customer.contacts.length > 0
                    ? customer.contacts
                    : form.contacts,
              });
            } else {
              setError("Customer not found");
            }
          } else {
            setError("Failed to load customers");
          }
        } catch (err) {
          setError("Network error");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      loadCustomer();
    }
  }, [id, isEdit, userId]);

  // Auto-fill display name
  useEffect(() => {
    const display =
      `${form.salutation} ${form.firstName} ${form.lastName}`.trim();
    if (display && display !== "Mr." && display !== form.displayName) {
      setForm((prev) => ({ ...prev, displayName: display }));
    }
  }, [form.salutation, form.firstName, form.lastName]);

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Invalid email";
    if (!form.displayName.trim())
      newErrors.displayName = "Display Name is required";
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan))
      newErrors.pan = "Invalid PAN (e.g. ABCDE1234F)";
    if (form.billing.pincode && !/^\d{6}$/.test(form.billing.pincode))
      newErrors.billingPincode = "Billing PIN must be 6 digits";
    if (form.shipping.pincode && !/^\d{6}$/.test(form.shipping.pincode))
      newErrors.shippingPincode = "Shipping PIN must be 6 digits";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddressChange = (type, e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [type]: { ...prev[type], [name]: value },
    }));
  };

  const copyBillingToShipping = () => {
    setForm((prev) => ({ ...prev, shipping: { ...prev.billing } }));
  };

  const addContact = () => {
    setForm((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          salutation: "Mr.",
          firstName: "",
          lastName: "",
          email: "",
          workPhone: "",
          mobile: "",
        },
      ],
    }));
  };

  const updateContact = (idx, field, value) => {
    setForm((prev) => {
      const newContacts = [...prev.contacts];
      newContacts[idx][field] = value;
      return { ...prev, contacts: newContacts };
    });
  };

  const removeContact = (idx) => {
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    // if (!validate() || !userId) {
    //   setError("Please fix errors and log in");
    //   return;
    // }

    setSaving(true);
    setError("");

    const payload = {
      ...form,
      userId,
      _id: isEdit ? id : undefined,
      receivables: form.openingBalance || "0.00",
      unusedCredits: "0.00",
    };

    try {
      const res = await fetch(`${backendUrl}/api/customers/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        alert("Customer saved successfully!");
        navigate("/sales/customers");
      } else {
        setError(result.error || "Failed to save");
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="customer-data-container">
        <div className="form-card">
          <h2>Loading customer...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-data-container">
      <div className="form-card">
        <h2>{isEdit ? "Edit Customer" : "New Customer"}</h2>

        {error && (
          <div style={{ color: "red", margin: "10px 0", fontWeight: "bold" }}>
            {error}
          </div>
        )}

        {/* === YOUR FULL FORM UI BELOW (unchanged) === */}
        {/* Customer Type */}
        <div className="form-row">
          <label>
            Customer Type <span className="req">*</span>
          </label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="customerType"
                value="Business"
                checked={form.customerType === "Business"}
                onChange={handleChange}
              />{" "}
              Business
            </label>
            <label>
              <input
                type="radio"
                name="customerType"
                value="Individual"
                checked={form.customerType === "Individual"}
                onChange={handleChange}
              />{" "}
              Individual
            </label>
          </div>
        </div>

        {/* Primary Contact */}
        <div className="form-row">
          <label>
            Primary Contact <span className="req">*</span>
          </label>
          <div className="inline-fields">
            <select
              name="salutation"
              value={form.salutation}
              onChange={handleChange}
            >
              <option>Mr.</option>
              <option>Mrs.</option>
              <option>Ms.</option>
              <option>Miss</option>
              <option>Dr.</option>
            </select>
            <input
              type="text"
              placeholder="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
            />
            <input
              type="text"
              placeholder="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
            />
          </div>
          {errors.firstName && (
            <span className="error">{errors.firstName}</span>
          )}
        </div>

        <div className="form-row">
          <label>Company Name</label>
          <input
            type="text"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
          />
        </div>

        <div className="form-row">
          <label>
            Display Name <span className="req">*</span>
          </label>
          <input
            type="text"
            name="displayName"
            value={form.displayName}
            onChange={handleChange}
          />
          {errors.displayName && (
            <span className="error">{errors.displayName}</span>
          )}
        </div>

        <div className="form-row">
          <label>
            Email Address <span className="req">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
          />
          {errors.email && <span className="error">{errors.email}</span>}
        </div>

        <div className="form-row">
          <label>Phone</label>
          <div className="inline-fields">
            <input
              type="tel"
              placeholder="Work Phone"
              name="workPhone"
              value={form.workPhone}
              onChange={handleChange}
            />
            <input
              type="tel"
              placeholder="Mobile"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* TABS */}
        <div className="tabs">
          {["other", "address", "contacts", "custom", "tags", "remarks"].map(
            (tab) => (
              <button
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "other"
                  ? "Other Details"
                  : tab === "address"
                  ? "Address"
                  : tab === "contacts"
                  ? "Contact Persons"
                  : tab === "custom"
                  ? "Custom Fields"
                  : tab === "tags"
                  ? "Reporting Tags"
                  : "Remarks"}
              </button>
            )
          )}
        </div>

        <div className="tab-content">
          {/* OTHER DETAILS */}
          {activeTab === "other" && (
            <>
              <div className="form-row">
                <label>PAN</label>
                <input
                  type="text"
                  placeholder="e.g. ABCDE1234F"
                  name="pan"
                  value={form.pan}
                  onChange={handleChange}
                />
                {errors.pan && <span className="error">{errors.pan}</span>}
              </div>

              <div className="form-row">
                <label>Currency</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} - {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label>Opening Balance</label>
                <div className="currency-input">
                  <span>{form.currency}</span>
                  <input
                    type="number"
                    name="openingBalance"
                    value={form.openingBalance}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>Payment Terms</label>
                <select
                  name="paymentTerms"
                  value={form.paymentTerms}
                  onChange={handleChange}
                >
                  <option>Due on Receipt</option>
                  <option>Net 15</option>
                  <option>Net 30</option>
                  <option>Net 60</option>
                </select>
              </div>

              <div className="form-row checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="enablePortal"
                    checked={form.enablePortal}
                    onChange={handleChange}
                  />
                  Allow portal access for this customer
                </label>
              </div>
            </>
          )}

          {/* ADDRESS TAB */}
          {activeTab === "address" && (
            <div className="address-grid">
              {/* Billing */}
              <div className="address-col">
                <h3>Billing Address</h3>
                <div className="form-row">
                  <label>Attention</label>
                  <input
                    type="text"
                    name="attention"
                    value={form.billing.attention}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Country/Region</label>
                  <select
                    name="country"
                    value={form.billing.country}
                    onChange={(e) => handleAddressChange("billing", e)}
                  >
                    {countries.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Address</label>
                  <div className="address-lines">
                    <input
                      type="text"
                      placeholder="Street 1"
                      name="street1"
                      value={form.billing.street1}
                      onChange={(e) => handleAddressChange("billing", e)}
                    />
                    <input
                      type="text"
                      placeholder="Street 2"
                      name="street2"
                      value={form.billing.street2}
                      onChange={(e) => handleAddressChange("billing", e)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={form.billing.city}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>State</label>
                  <select
                    name="state"
                    value={form.billing.state}
                    onChange={(e) => handleAddressChange("billing", e)}
                  >
                    {indiaStates.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={form.billing.pincode}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                  {errors.billingPincode && (
                    <span className="error">{errors.billingPincode}</span>
                  )}
                </div>
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.billing.phone}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Fax Number</label>
                  <input
                    type="text"
                    name="fax"
                    value={form.billing.fax}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
              </div>

              {/* Shipping */}
              <div className="address-col">
                <h3>
                  Shipping Address
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={copyBillingToShipping}
                  >
                    Copy Billing Address
                  </button>
                </h3>
                <div className="form-row">
                  <label>Attention</label>
                  <input
                    type="text"
                    name="attention"
                    value={form.shipping.attention}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Country/Region</label>
                  <select
                    name="country"
                    value={form.shipping.country}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  >
                    {countries.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Address</label>
                  <div className="address-lines">
                    <input
                      type="text"
                      placeholder="Street 1"
                      name="street1"
                      value={form.shipping.street1}
                      onChange={(e) => handleAddressChange("shipping", e)}
                    />
                    <input
                      type="text"
                      placeholder="Street 2"
                      name="street2"
                      value={form.shipping.street2}
                      onChange={(e) => handleAddressChange("shipping", e)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={form.shipping.city}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>State</label>
                  <select
                    name="state"
                    value={form.shipping.state}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  >
                    {indiaStates.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={form.shipping.pincode}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                  {errors.shippingPincode && (
                    <span className="error">{errors.shippingPincode}</span>
                  )}
                </div>
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.shipping.phone}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Fax Number</label>
                  <input
                    type="text"
                    name="fax"
                    value={form.shipping.fax}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CONTACT PERSONS */}
          {activeTab === "contacts" && (
            <div className="contact-section">
              <button
                type="button"
                className="add-contact-btn"
                onClick={addContact}
              >
                + Add Contact Person
              </button>
              {form.contacts.map((c, i) => (
                <div key={i} className="contact-row">
                  <select
                    value={c.salutation}
                    onChange={(e) =>
                      updateContact(i, "salutation", e.target.value)
                    }
                  >
                    <option>Mr.</option>
                    <option>Mrs.</option>
                    <option>Ms.</option>
                    <option>Miss</option>
                    <option>Dr.</option>
                  </select>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={c.firstName}
                    onChange={(e) =>
                      updateContact(i, "firstName", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={c.lastName}
                    onChange={(e) =>
                      updateContact(i, "lastName", e.target.value)
                    }
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={c.email}
                    onChange={(e) => updateContact(i, "email", e.target.value)}
                  />
                  <input
                    type="tel"
                    placeholder="Work Phone"
                    value={c.workPhone}
                    onChange={(e) =>
                      updateContact(i, "workPhone", e.target.value)
                    }
                  />
                  <input
                    type="tel"
                    placeholder="Mobile"
                    value={c.mobile}
                    onChange={(e) => updateContact(i, "mobile", e.target.value)}
                  />
                  {form.contacts.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeContact(i)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === "custom" && (
            <p className="empty">No custom fields added yet.</p>
          )}
          {activeTab === "tags" && (
            <p className="empty">No reporting tags added.</p>
          )}
          {activeTab === "remarks" && (
            <div className="form-row">
              <label>Remarks</label>
              <textarea
                rows="5"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                placeholder="Internal notes..."
              ></textarea>
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="form-actions">
          <button className="save-btn" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            className="cancel-btn"
            onClick={() => navigate("/sales/customers")}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerData;
